import React, { useState, useEffect } from 'react';
import ForecastChart from './ForecastChart';
import forecastingService, {
  ForecastResultUI,
  HistoricalDataPoint,
  ForecastSummaryNew,
  formatCurrency,
  formatPercentage,
  getConfidenceLevelLabel,
} from '../services/forecastingService';

type ConfidenceLevel = 90 | 95 | 99;

const ForecastingDashboard: React.FC = () => {
  // State management
  const [daysToForecast, setDaysToForecast] = useState<number>(30);
  const [confidenceLevel, setConfidenceLevel] = useState<ConfidenceLevel>(95);
  const [alpha, setAlpha] = useState<number>(0.3); // smoothing parameter
  const [daysBack, setDaysBack] = useState<number>(90);
  const [showConfidenceBands, setShowConfidenceBands] = useState<boolean>(true);

  // Data state
  const [forecastResult, setForecastResult] = useState<ForecastResultUI | null>(null);
  const [historicalData, setHistoricalData] = useState<HistoricalDataPoint[]>([]);
  const [forecastSummary, setForecastSummary] = useState<ForecastSummaryNew | null>(null);

  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // removed comparison (legacy models) – backend now provides hybrid forecasting only

  // Load historical data on mount
  useEffect(() => {
    loadHistoricalData();
    loadForecastSummary();
  }, [daysBack]);

  const loadHistoricalData = async () => {
    try {
      const data = await forecastingService.getHistoricalRevenue(daysBack);
      setHistoricalData(data);
    } catch (err: any) {
      console.error('Failed to load historical data:', err);
      setError(err.message || 'Failed to load historical revenue data');
    }
  };

  const loadForecastSummary = async () => {
    try {
      const summary = await forecastingService.getForecastSummary();
      setForecastSummary(summary);
    } catch (err: any) {
      console.error('Failed to load forecast summary:', err);
    }
  };

  const handleGenerateForecast = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await forecastingService.generateHybridForecast({
        forecastDays: daysToForecast,
        alpha,
        confidenceLevel,
        saveToDatabase: true,
      });
      setForecastResult(result);
      await loadForecastSummary();
    } catch (err: any) {
      console.error('Failed to generate forecast:', err);
      setError(err.message || 'Failed to generate forecast');
    } finally {
      setLoading(false);
    }
  };

  // Legacy comparison removed – placeholder for future multi-model support

  const handleExportData = () => {
    if (!forecastResult) return;

    const csvData = [
      ['Date', 'Predicted Revenue', 'Confidence Low', 'Confidence High'],
      ...forecastResult.forecasts.map((f) => [
        f.date,
        f.predictedRevenue.toString(),
        f.confidenceLow.toString(),
        f.confidenceHigh.toString(),
      ]),
    ];

    const csvContent = csvData.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
  a.download = `revenue-forecast-hybrid-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Revenue Forecasting
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Predict future revenue using statistical models and historical data
        </p>
      </div>

      {/* Summary Cards */}
      {forecastSummary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Current Monthly Revenue</h3>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(forecastSummary.currentMonthlyRevenue)}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Predicted Next Month</h3>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(forecastSummary.predictedNextMonth)}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Predicted (Next 3 Months)</h3>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(forecastSummary.predictedNext3Months)}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Confidence Level</h3>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {forecastSummary.confidenceLevel}%
            </p>
          </div>
        </div>
      )}

      {/* Settings Panel */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Forecast Settings
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Smoothing Alpha */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Alpha (Smoothing)</label>
            <input
              type="number"
              min="0"
              max="1"
              step="0.05"
              value={alpha}
              onChange={(e) => setAlpha(parseFloat(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Days to Forecast */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Forecast Period (days)
            </label>
            <input
              type="number"
              min="7"
              max="365"
              value={daysToForecast}
              onChange={(e) => setDaysToForecast(parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Confidence Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Confidence Level
            </label>
            <select
              value={confidenceLevel}
              onChange={(e) => setConfidenceLevel(parseInt(e.target.value) as ConfidenceLevel)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value={90}>90%</option>
              <option value={95}>95%</option>
              <option value={99}>99%</option>
            </select>
          </div>

          {/* Historical Data Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Historical Data (days)
            </label>
            <input
              type="number"
              min="30"
              max="365"
              value={daysBack}
              onChange={(e) => setDaysBack(parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mt-6">
          <button
            onClick={handleGenerateForecast}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
          >
            {loading ? 'Generating...' : 'Generate Forecast'}
          </button>

          {/* Compare Models removed (hybrid only) */}

          {forecastResult && (
            <button
              onClick={handleExportData}
              className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
            >
              Export CSV
            </button>
          )}

          <label className="flex items-center gap-2 ml-auto">
            <input
              type="checkbox"
              checked={showConfidenceBands}
              onChange={(e) => setShowConfidenceBands(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Show Confidence Bands</span>
          </label>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Single Model Forecast */}
  {forecastResult && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <ForecastChart
            historical={historicalData}
            forecasts={forecastResult.forecasts}
            model={'hybrid'}
            showConfidenceBands={showConfidenceBands}
            height={500}
          />

          {/* Forecast Summary (hybrid) */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Current Monthly Revenue</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(forecastResult.summary.currentMonthlyRevenue)}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Predicted Next Month</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(forecastResult.summary.predictedNextMonth)}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Predicted Next 3 Months</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(forecastResult.summary.predictedNext3Months)}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Confidence Level</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {getConfidenceLevelLabel(forecastResult.summary.confidenceLevel)}
              </p>
            </div>
          </div>

          {/* Accuracy Metrics */}
          {forecastResult.accuracy && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Model Accuracy
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">MAPE</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatPercentage(forecastResult.accuracy.mape)}
                  </p>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">RMSE</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(forecastResult.accuracy.rmse)}
                  </p>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">R² Score</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {forecastResult.accuracy.r2.toFixed(4)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default ForecastingDashboard;
